import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appearance", "children", "defaultSelected", "fluid", "manualActivation", "onSelected", "selected"];
import PropTypes from 'prop-types';
import React, { Children, cloneElement, forwardRef, useCallback, useContext, useRef } from 'react';
import { KEY_CODES, TABS_APPEARANCES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { TabsContext } from '../Tabs/Tabs.context';
import { focusNextTab, focusPreviousTab } from '../Tabs/tabs.focusTab';
import { StyledTabList } from './TabList.styles';

/**
 * A `Tabs.List` is a collection of `Tabs.Tab` components. Similiar to `Radio` controls,
 * the tabs in a `Tabs.List` are **mutually exclusive** -- only one `Tabs.Tab` can be
 * selected at any given time.
 *
 * @extends ToggleButtonGroup
 * @since 8.10.0
 */
export var TabList = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    children = _ref.children,
    defaultSelected = _ref.defaultSelected,
    fluid = _ref.fluid,
    manualActivation = _ref.manualActivation,
    onSelected = _ref.onSelected,
    selected = _ref.selected,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(TabsContext);
  var defaultInnerRef = useRef();
  var innerRef = forwardedRef || defaultInnerRef;
  var onSelectedCallback = useCallback(function (id) {
    // If uncontrolled and is a child of `Tabs`, update the `Tabs` internal state.
    if (typeof context.selectedState !== 'undefined') {
      context.setSelectedState(id);
    }
    if (context.onSelected) {
      context.onSelected(id);
    }
    if (onSelected) {
      onSelected(id);
    }
  }, [context, onSelected]);
  var onKeyDownCallback = useCallback(function (e) {
    var selectedTab;
    if (e.keyCode === KEY_CODES.ARROW_RIGHT) {
      selectedTab = focusNextTab(innerRef.current, false);
      e.preventDefault();
    } else if (e.keyCode === KEY_CODES.ARROW_LEFT) {
      selectedTab = focusPreviousTab(innerRef.current, true);
      e.preventDefault();
    }
    if (!context.manualActivation && !manualActivation && typeof selectedTab !== 'undefined') {
      selectedTab.click();
    }
  }, [context.manualActivation, manualActivation, innerRef]);
  return /*#__PURE__*/React.createElement(StyledTabList, _extends({
    appearance: context.appearance || appearance,
    defaultValue: context.selectedState || defaultSelected,
    fluid: typeof context.fluid !== 'undefined' ? context.fluid : fluid,
    onChange: onSelectedCallback,
    onKeyDown: onKeyDownCallback,
    ref: innerRef,
    role: "tablist",
    value: context.selected || selected
  }, rest), Children.map(children, function (child) {
    return (
      // Provide auto-generated `aria-controls` if missing.
      // Add `value` for use by ToggleButtonGroup
      child && /*#__PURE__*/cloneElement(child, {
        'aria-controls': typeof child.props['aria-controls'] !== 'undefined' ? child.props['aria-controls'] : child.props.id + "_panel",
        'value': child.props.id
      })
    );
  }));
});
TabList.displayName = 'Tabs.List';
TabList.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The style of `Tabs.Tab` to display. Can be overridden by
   * `appearance` set on the parent `Tabs` component.
   */
  'appearance': PropTypes.oneOf(Object.values(TABS_APPEARANCES)),
  /**
   * An [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) is not required
   * but may be used to describe the `Tabs` instance.
   */
  'aria-label': PropTypes.string,
  /**
   * A group of `Tabs.Tab` components.
   */
  'children': PropTypes.node.isRequired,
  /**
   * The id of the `Tabs.Tab` to select by default.
   * When set, `Tabs.List` will behave as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * If `Tabs.List` is a child of `Tabs`, set `defaultSelected` on
   * `Tabs` instead. Otherwise, `Tabs.List` must be set with either
   * `selected` or `defaultSelected`, but not both.
   */
  'defaultSelected': PropTypes.string,
  /**
   * When `true`, the `Tabs.List` stretches to fill its container and tries to maintain equal
   * tab widths. Can be overridden by `fluid` set on the
   * parent `Tabs` component.
   */
  'fluid': CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * Align tabs within `Tabs.List`
   */
  'justifyContent': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'])),
  /**
   * Determines the behavior when navigating the `Tabs.List` with arrow keys. By default, when a
   * new `Tabs.Tab` is given focus, its corresponding `Tabs.Panel`
   * will display automatically. When `manualActivation` is set to "true",
   * the user must press the Spacebar or Enter key before the panel is displayed.
   *
   * In circumstances where content can be displayed instantly (i.e., all `Tabs.Panel` content is
   * present in the DOM), use the default behavior. If changing panels requires first loading the
   * content, set `manualActivation` to "true".
   * https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_selection_follows_focus
   */
  'manualActivation': PropTypes.bool,
  /**
   * Callback for when a new tab is selected. Receives the `id` of the selected
   * `Tabs.Tab`.
   */
  'onSelected': PropTypes.func,
  /**
   * The id of the selected `Tabs.Tab`. When set,
   * `Tabs.List` will behave as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * If `Tabs.List` is a child of `Tabs`, set `selected` on
   * `Tabs` instead.
   * Otherwise, `Tabs.List` must be set with either `selected` or `defaultSelected`, but not both.
   */
  'selected': PropTypes.string
} : {};
TabList.defaultProps = {
  appearance: TABS_APPEARANCES.default
};